/**
 * LocationFilters Component
 * Filter orders by delivery location (City and State)
 * Supports Indian cities and all states/UTs
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 4 (AC-6)
 */

import * as React from 'react';
import { MapPin } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// ============================================
// CONSTANTS - INDIAN LOCATIONS
// ============================================

/**
 * Major Indian cities (alphabetical)
 */
const INDIAN_CITIES = [
  'Ahmedabad',
  'Bangalore',
  'Bhopal',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Delhi',
  'Goa',
  'Gurgaon',
  'Hyderabad',
  'Indore',
  'Jaipur',
  'Kochi',
  'Kolkata',
  'Lucknow',
  'Mumbai',
  'Nagpur',
  'Pune',
  'Surat',
  'Vadodara',
  'Visakhapatnam',
] as const;

/**
 * Indian States (28 states)
 */
const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
] as const;

/**
 * Union Territories (8 UTs)
 */
const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

/**
 * Combined list of all states and UTs
 */
const ALL_STATES = [...INDIAN_STATES, ...UNION_TERRITORIES].sort();

// ============================================
// INTERFACE
// ============================================

export interface LocationFiltersProps {
  selectedCity: string | null;
  selectedState: string | null;
  onLocationChange: (city: string | null, state: string | null) => void;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const LocationFilters = React.memo(function LocationFilters({
  selectedCity,
  selectedState,
  onLocationChange,
  className,
}: LocationFiltersProps) {
  // Handle city change
  const handleCityChange = React.useCallback(
    (value: string) => {
      const newCity = value === 'ALL' ? null : value;
      onLocationChange(newCity, selectedState);
    },
    [onLocationChange, selectedState]
  );

  // Handle state change
  const handleStateChange = React.useCallback(
    (value: string) => {
      const newState = value === 'ALL' ? null : value;
      onLocationChange(selectedCity, newState);
    },
    [onLocationChange, selectedCity]
  );

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <MapPin
          className="h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Label className="text-sm font-medium text-foreground">
          Location
        </Label>
      </div>

      {/* Filter Inputs - Compact inline layout */}
      <div className="grid grid-cols-2 gap-2">
        {/* City Filter */}
        <Select
          value={selectedCity || 'ALL'}
          onValueChange={handleCityChange}
        >
          <SelectTrigger
            id="city-filter"
            className="w-full"
            aria-label="Filter by city"
          >
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Cities</SelectItem>
            {INDIAN_CITIES.map((city) => (
              <SelectItem key={city} value={city}>
                {city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* State Filter */}
        <Select
          value={selectedState || 'ALL'}
          onValueChange={handleStateChange}
        >
          <SelectTrigger
            id="state-filter"
            className="w-full"
            aria-label="Filter by state or union territory"
          >
            <SelectValue placeholder="All States / UTs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All States / UTs</SelectItem>
            {ALL_STATES.map((state) => (
              <SelectItem key={state} value={state}>
                {state}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Accessibility announcement */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {selectedCity && selectedState
          ? `Filtering by ${selectedCity}, ${selectedState}`
          : selectedCity
          ? `Filtering by ${selectedCity}`
          : selectedState
          ? `Filtering by ${selectedState}`
          : 'No location filters applied'}
      </div>
    </div>
  );
});
